/**
 * Legend panel — right side of the layout.
 *
 * Two sections (Player Characters / NPCs). Each entry shows:
 *   - small portrait (img tag, native browser caching)
 *   - name
 *   - status text (italic) if set
 *   - "(downed)" tag when downed
 *
 * Each entry is HTML5 `draggable=true` so the user can drag a token from the
 * legend onto the canvas to (re)place it. dataTransfer carries the token id
 * under MIME type 'application/x-mapnmove-token'.
 */

import { openTokenDeleteMenu } from './contextMenu.js';
import type { Token } from './types.js';

export const LEGEND_DRAG_MIME = 'application/x-mapnmove-token';
const DEFAULT_NPC_IMAGE = '/default-npc.svg';

/**
 * Optional callback invoked when a legend entry is clicked (not dragged).
 * Browsers don't fire `click` after a completed drag, so this won't collide
 * with the drag-to-place flow.
 */
let onTokenClick: ((token: Token) => void) | null = null;

export function mountLegend(
  root: HTMLElement,
  tokenClickHandler?: (token: Token) => void,
): { render: (tokens: readonly Token[]) => void } {
  onTokenClick = tokenClickHandler ?? null;
  return {
    render(tokens) {
      renderLegend(root, tokens);
    },
  };
}

function renderLegend(root: HTMLElement, tokens: readonly Token[]): void {
  if (tokens.length === 0) {
    root.innerHTML = '<p class="legend-empty">Tokens will appear here.</p>';
    return;
  }

  const pcs = tokens.filter((t) => t.type === 'pc');
  const npcs = tokens.filter((t) => t.type === 'npc');

  // Build via DOM APIs so we don't have to escape user-controlled fields.
  root.replaceChildren(
    legendSection('Player Characters', pcs),
    legendSection('NPCs', npcs),
  );
}

function legendSection(title: string, tokens: readonly Token[]): HTMLElement {
  const section = document.createElement('section');
  section.className = 'legend-section';

  const heading = document.createElement('h2');
  heading.textContent = `${title} (${tokens.length})`;
  section.appendChild(heading);

  if (tokens.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'legend-empty';
    empty.textContent = 'None';
    section.appendChild(empty);
    return section;
  }

  const list = document.createElement('ul');
  list.className = 'legend-list';
  for (const t of tokens) {
    list.appendChild(legendEntry(t));
  }
  section.appendChild(list);
  return section;
}

function legendEntry(token: Token): HTMLElement {
  const li = document.createElement('li');
  li.className = `legend-entry legend-${token.type}`;
  if (token.is_downed) li.classList.add('is-downed');
  li.draggable = true;
  li.dataset.tokenId = String(token.id);

  li.addEventListener('dragstart', (e) => {
    if (e.dataTransfer === null) return;
    e.dataTransfer.setData(LEGEND_DRAG_MIME, String(token.id));
    e.dataTransfer.effectAllowed = 'move';
  });

  // Click (without a drag) recenters the camera on this token.
  li.addEventListener('click', () => {
    onTokenClick?.(token);
  });

  // Right-click opens a destructive "Delete token" menu.
  li.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    openTokenDeleteMenu(token, e.clientX, e.clientY);
  });

  const img = document.createElement('img');
  img.className = 'legend-portrait';
  img.alt = '';
  img.src = token.image_path !== null ? `/${token.image_path}` : DEFAULT_NPC_IMAGE;
  li.appendChild(img);

  const meta = document.createElement('div');
  meta.className = 'legend-meta';

  const name = document.createElement('div');
  name.className = 'legend-name';
  name.textContent = token.name;
  meta.appendChild(name);

  if (token.status_text !== null && token.status_text !== '') {
    const status = document.createElement('div');
    status.className = 'legend-status';
    status.textContent = token.status_text;
    meta.appendChild(status);
  }

  if (token.is_downed) {
    const downed = document.createElement('div');
    downed.className = 'legend-downed';
    downed.textContent = '(downed)';
    meta.appendChild(downed);
  }

  if (!token.is_placed) {
    const unplaced = document.createElement('div');
    unplaced.className = 'legend-unplaced';
    unplaced.textContent = '(not on map — drag to place)';
    meta.appendChild(unplaced);
  }

  li.appendChild(meta);
  return li;
}
